import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { normalizeColumnSettings, getLogoSlotDimensions, getSignatureSlotDimensions, getObjectFit, DEFAULT_DOCUMENT_SETTINGS } from '../../../Utils/documentSettingsDefaults';

const DocumentPreview = ({ 
    t,
    client, 
    settings, 
    selectedDocumentType, 
    signaturePreview, 
    profilePreview,
    direction,
    defaultItem = null
}) => {

    
    const generatePreviewHtml = useMemo(() => {
        if (!client) return '';
        
        // When settings is null/undefined (e.g. documentSettings not set), use defaults — matches backend center logo default
        const safeSettings = settings ?? DEFAULT_DOCUMENT_SETTINGS;
        const {
            customDescription,
            customFooter,
            headerRightText,
            headerLeftText,
            itemsDisplayMode,
            columnSettings
        } = safeSettings;
        
        // Get text direction and alignment based on direction prop
        const isLTR = direction === 'ltr';
        const aling = isLTR ? 'left' : 'right';
        
        // Normalize column settings to ensure all values are valid (handles null/undefined columnSettings)
        const normalizedSettings = normalizeColumnSettings(columnSettings ?? safeSettings.columnSettings);
        
        const {
            textSize,
            companyInfoTextSize,
            documentTitleTextSize,
            profilePictureShape,
            boldDate,
            width,
            height,
            backgroundColor,
            textColor,
            upperPanelColor,
            logoPosition,
            dateAlign,
            titleTextColor,
            totalsLayout,
            documentFont,
        } = normalizedSettings;

        // Match original design: font name only (no generic fallback), same as original .haktsaa1 { font-family: Montserrat; }
        const documentFontName = documentFont || 'Montserrat';
        const fontFamilyCss = documentFontName;
        // Get logo slot dimensions (converted to px for preview)
        const logoSlot = getLogoSlotDimensions(normalizedSettings);
        const signatureSlot = getSignatureSlotDimensions(normalizedSettings);
        const objectFit = getObjectFit(normalizedSettings);

        // Whether we actually have a logo to render.
        // When there is no logo, drop the logo column entirely so the header
        // text sections align to the visual edges of the page (matches the
        // intent of the headerLeftText / headerRightText field names).
        const hasLogo = !!profilePreview;
        
        // Build logo image with proper containment
        // The image will be 100% of the slot size, with object-fit ensuring it fits
        const logoImageStyle = `width: 100%; height: 100%; object-fit: ${objectFit}; display: block;`;
        
        // Build shape-specific wrapper styles
        const getShapeWrapperStyle = () => {
            switch (profilePictureShape) {
                case "rectangle":
                    return `border: 1px solid #ccc;`;
                case "roundedRectangle":
                    return `border-radius: 12px; border: 1px solid #ccc;`;
                case "oval":
                case "circle":
                    return `border-radius: 50%;`;
                case "hexagon":
                    return `clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);`;
                case "diamond":
                    return `clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);`;
                case "triangle":
                    return `clip-path: polygon(50% 0%, 0% 100%, 100% 100%);`;
                default:
                    return ``;
            }
        };
        
        const shapeWrapperStyle = getShapeWrapperStyle();
        
        // Build logo HTML with proper containment structure
        const logo = profilePreview ? `
            <div class="logo-mask" style="width: 100%; height: 100%; overflow: hidden; ${shapeWrapperStyle}">
                <img src='${profilePreview}' alt='Profile Picture' style='${logoImageStyle}' />
            </div>
        ` : '';
        
        // Apply date styling
        const dateStyle = boldDate ? 'font-weight: bold;' : '';
        
        // Document type labels
        const documentTypeLabels = {
            'TaxInvoice': 'חשבונית מס',
            'ReceiptTax': 'חשבונית מס קבלה',
            'CreditNote': 'חשבונית מס זיכוי',
            'Receipt': 'קבלה',
            'Quotation': 'הצעת מחיר',
            'Order': 'הזמנה',
            'Transaction': 'חשבון עסקה',
            'PostponedReceipt': 'קבלת דחויים',
            'Deposit': 'משיכה/הפקדה'
        };
        
        const title = documentTypeLabels[selectedDocumentType] || 'מסמך';
        
        // Determine if document should show table
        const shouldShowTable = ['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(selectedDocumentType);
        
        // Determine ID label based on case type (simplified for preview)
        const idLabel = 'ח"פ\\ע"מ';
        
        // Sample data for preview: use defaultItem if provided, else hardcoded sample
        const vatRate = 0.18;
        let sampleItems;
        let totalPrice;
        let tax;
        let totalPriceWithTax;
        if (defaultItem && (defaultItem.makat || defaultItem.Makat)) {
            const priceVal = parseFloat(defaultItem.price ?? defaultItem.Price ?? 0) || 0;
            const includeVat = defaultItem.includeVat ?? defaultItem.IncludeVat ?? true;
            sampleItems = [
                {
                    makat: defaultItem.makat ?? defaultItem.Makat ?? '001',
                    name: defaultItem.name ?? defaultItem.Name ?? 'מוצר/שירות לדוגמה',
                    quantity: '1',
                    price: String(priceVal),
                    discount: '0',
                    priceAfterDiscount: String(priceVal)
                }
            ];
            totalPrice = priceVal;
            tax = includeVat ? Math.round(priceVal * vatRate * 100) / 100 : 0;
            totalPriceWithTax = totalPrice + tax;
        } else {
            sampleItems = [
                {
                    makat: '001',
                    name: 'מוצר/שירות לדוגמה',
                    quantity: '1',
                    price: '1000',
                    discount: '0',
                    priceAfterDiscount: '1000'
                }
            ];
            totalPrice = 1000;
            tax = 180;
            totalPriceWithTax = 1180;
        }
        
        // Footer HTML matching C# design exactly
        const footer = `
            <div style="width: 100%; height: 20px; text-align: center; background: #304FFF; -webkit-print-color-adjust: exact; display: flex; flex-direction: row; align-items: center; justify-content: center; padding: 5px; position: absolute; bottom: 0; left: 0;">
                <p style="margin: 0; font-family: ${fontFamilyCss}; font-size: 10px; font-weight: bold; line-height: 1; color: white;">
                    <span style="font-weight: bold;">מסמך זה הופק במערכת</span> 
                    <span style="font-weight: bold;">Xtram</span> | 
                    טל: 04-6590004 | 
                    <a href="https://www.xtram.co.il" style="color: white; text-decoration: none; font-weight: bold;">www.xtram.co.il</a>
                </p>
            </div>
        `;
        
        // Determine CSS Grid template based on logo position and direction.
        // We keep the same column TYPES the backend uses in production
        // (auto / fixed-px / 1fr) so Puppeteer rendering on Linux is
        // identical. We only re-order which item lands in which column so
        // headerLeftText ends up on the visual LEFT of the page.
        const getHeaderGridTemplate = () => {
            if (!hasLogo) {
                // No logo: 2-column layout so the two text sections fill the
                // header symmetrically and land on opposite visual edges.
                return `grid-template-columns: 1fr 1fr;`;
            }
            if (logoPosition === 'left') {
                // Left logo layout (3-column). Visual order in BOTH directions:
                //   [headerLeftText (auto)] [logo (logoWidth px)] [company info (1fr)]
                if (isLTR) {
                    // LTR source = visual order (left → right).
                    return `grid-template-columns: auto ${logoSlot.widthPx}px 1fr;`;
                }
                // RTL: source columns are visually reversed, so to get the
                // same [text][logo][company] visual we put 1fr first and
                // auto last.
                return `grid-template-columns: 1fr ${logoSlot.widthPx}px auto;`;
            }
            // Center logo: hardcoded px only (no fr/auto) so Linux Puppeteer does not hang — matches backend
            const HEADER_GRID_WIDTH_PX = 800;
            const sidePx = Math.max(0, Math.floor((HEADER_GRID_WIDTH_PX - logoSlot.widthPx) / 2));
            return `grid-template-columns: ${sidePx}px ${logoSlot.widthPx}px ${sidePx}px;`;
        };
        
        // Determine grid order for elements based on logo position and direction
        const getGridOrder = (element) => {
            if (!hasLogo) {
                // 2-column layout: company on the inline-start (visual right
                // in RTL, visual left in LTR), header text on the inline-end
                // (visual left in RTL, visual right in LTR).
                return element === 'company' ? 1 : 2;
            }
            if (logoPosition === 'left') {
                if (isLTR) {
                    // LTR: header=1 (auto/left), logo=2 (middle), company=3 (1fr/right)
                    return element === 'header' ? 1 : element === 'logo' ? 2 : 3;
                }
                // RTL: company=1 (1fr/right), logo=2 (middle), header=3 (auto/left)
                return element === 'company' ? 1 : element === 'logo' ? 2 : 3;
            }
            // Center: company=1, logo=2, header=3
            return element === 'company' ? 1 : element === 'logo' ? 2 : 3;
        };
        
        const headerGridTemplate = getHeaderGridTemplate();

        // Use display: table with percentage-width cells (matches the
        // production backend) for any case that previously caused Linux
        // Puppeteer to hang on grid + gap + fr/auto combinations. Two cases
        // qualify:
        //   1. center logo + has logo (existing behavior)
        //   2. no logo at all — the 1fr/auto grid was the new failure mode
        // Percentages always sum to 100%, so nothing overflows or gets clipped.
        // The "left logo" case keeps the simpler grid (it is small and was
        // confirmed working in production).
        const useTableLayoutForCenter = hasLogo && logoPosition === 'center';
        const useTableLayout = useTableLayoutForCenter || !hasLogo;
        const HEADER_GRID_WIDTH_PX = 800;
        const centerSidePx = Math.max(0, Math.floor((HEADER_GRID_WIDTH_PX - logoSlot.widthPx) / 2));
        const centerSidePct = useTableLayoutForCenter ? (centerSidePx / HEADER_GRID_WIDTH_PX) * 100 : 0;
        const centerLogoPct = useTableLayoutForCenter ? (logoSlot.widthPx / HEADER_GRID_WIDTH_PX) * 100 : 0;
        // No-logo: split the header into two equal cells (company | header).
        const noLogoCellPct = 50;
        // Width of the side text cells in table mode.
        const sideCellPct = useTableLayoutForCenter ? centerSidePct : noLogoCellPct;

        const upperPanelDetailsInlineStyle = useTableLayout
            ? `display: table; width: 100%; height: 140px; border-collapse: collapse; direction: ${direction}; padding: 0 10px; table-layout: fixed;`
            : '';

        const companyInlineStyle = useTableLayout
            ? `display: table-cell; width: ${sideCellPct.toFixed(1)}%; vertical-align: middle; padding: 0 5px;`
            : `order: ${getGridOrder('company')};`;

        const logoInlineStyle = useTableLayoutForCenter
            ? `display: table-cell; width: ${centerLogoPct.toFixed(1)}%; vertical-align: middle; padding: 0;`
            : `order: ${getGridOrder('logo')};`;

        const headerInlineStyle = useTableLayout
            ? `display: table-cell; width: ${sideCellPct.toFixed(1)}%; vertical-align: middle; padding: 0 5px;`
            : `order: ${getGridOrder('header')};`;
        
        const htmlContent = `
            <!DOCTYPE html>
            <html lang="he" dir="rtl">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>${title}</title>
                <style>
                    @page {
                        size: A4;
                    }
                    
                    * {
                        box-sizing: border-box;
                    }
                    
                    body {
                        margin: 0;
                        padding: 0;
                        font-size: ${textSize}px;
                        background-color: ${backgroundColor};
                        color: ${textColor};
                        background-color: rgba(255, 255, 255, 0);
                        font-family: ${fontFamilyCss};
                        
                        display: -webkit-box;
                        display: -moz-box;
                        display: -ms-flexbox;
                        display: -webkit-flex;
                        display: flex;

                        align-items: center;
                    }
                    
                    .a4 {
                        width: 100%;
                        height: 200mm;
                        box-sizing: border-box;
                        margin: 0 auto;
                        position: relative;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: flex-start;
                        background-color: ${backgroundColor};
                        transform: scale(0.8);
                        transform-origin: top center;
                    }
                    
                    .upper-panel {
                        width: 100%;
                        background-color: ${upperPanelColor};
                        border-bottom: 1px solid #ccc;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    
                    .upper-panel-details {
                        direction: ${direction};
                        width: 100%;
                        height: 140px;
                        display: grid;
                        ${headerGridTemplate}
                        align-items: center;
                        gap: 10px;
                        padding: 0 10px;
                        position: relative;
                    }
                    
                    .logo-slot {
                        width: ${logoSlot.widthPx}px;
                        height: ${logoSlot.heightPx}px;
                        min-width: ${logoSlot.widthPx}px;
                        min-height: ${logoSlot.heightPx}px;
                        max-width: ${logoSlot.widthPx}px;
                        max-height: ${logoSlot.heightPx}px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                        flex-grow: 0;
                        position: relative;
                        margin: 0 auto;
                    }
                    
                    .logo-mask {
                        width: 100%;
                        height: 100%;
                        max-width: 100%;
                        max-height: 100%;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                    }
                    
                    .logo-mask img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        object-position: center;
                        display: block;
                    }
                    
                    .header-company-info {
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        min-width: 0;
                        overflow: hidden;
                        word-wrap: break-word;
                        word-break: break-word;
                    }
                    
                    .header-text-section {
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        min-width: 0;
                        overflow: hidden;
                        word-wrap: break-word;
                        word-break: break-word;
                        /* Push the text to the OUTER edge of its column so it
                         * lands on the visual far-side of the page (far-right
                         * in LTR, far-left in RTL). Using flex-end works in
                         * both writing modes because the flex container
                         * inherits direction: in LTR, flex-end is the right
                         * side; in RTL, flex-end is the left side. */
                        justify-content: flex-end;
                    }
                    
                    .title-from-to {
                        color: var(--Foundation-Blue-Normal, #304FFF);
                        font-family: ${fontFamilyCss};
                        font-size: 20px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: normal;
                    }
                    
                    .haktsaa {
                        color: var(--Foundation-Blue-Normal, #000000);
                        font-family: ${fontFamilyCss};
                        font-size: ${companyInfoTextSize}px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: normal;
                    }
                    
                    .haktsaa1 {
                        color: var(--Foundation-Blue-Normal, #000000);
                        font-family: ${fontFamilyCss};
                        font-size: ${documentTitleTextSize}px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: normal;
                    }
                    
                    .title-inner {
                        color: var(--Foundation-Yellow-Normal, #F9AA2A);
                        font-family: ${fontFamilyCss};
                        font-size: 20px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: normal;
                    }
                    
                    .title-inner-payment {
                        color: var(--Foundation-Yellow-Normal, #F9AA2A);
                        font-family: ${fontFamilyCss};
                        font-size: 25px;
                        font-style: normal;
                        font-weight: 600;
                        line-height: normal;
                        margin-bottom: 10px;
                    }
                    
                    .title-details {
                        color: var(--Drak-80, rgba(0, 0, 0, 0.80));
                        font-family: ${fontFamilyCss};
                        font-size: 13px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: normal;
                    }
                    
                    .from-to-divs {
                        border: 1px solid #878787;
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        position: relative;
                        padding: 10px;
                    }
                    
                    .section {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 100%;
                        text-align: center;
                    }
                    
                    .inner-section {
                        width: 92%;
                    }
                    
                    .dates_container {
                        display: -webkit-box;
                        display: -moz-box;
                        display: -ms-flexbox;
                        display: -webkit-flex;
                        display: flex;
                        
                        -webkit-box-orient: horizontal;
                        -moz-box-orient: horizontal;
                        -webkit-box-direction: reverse;
                        -moz-box-direction: reverse;
                        -webkit-flex-direction: row-reverse;
                        -ms-flex-direction: row-reverse;
                        flex-direction: row-reverse;
                        gap: 20px;
                    }
                    
                    table {
                        width: 100%;
                        border-collapse: separate;
                        border-spacing: 0;
                        direction: rtl;
                    }
                    
                    th, td {
                        padding: 8px;
                        text-align: center;
                    }
                    
                    th {
                        background-color: #E8EEFF;
                        color: #304FFF;
                        font-weight: bold;
                    }
                    
                    thead tr:first-child th:first-child {
                        border-top-right-radius: 8px;
                    }
                    
                    thead tr:first-child th:last-child {
                        border-top-left-radius: 8px;
                    }
                    
                    tbody tr {
                        background-color: #ffffff;
                    }
                    
                    tbody tr:hover {
                        background-color: #f8f9fa;
                    }
                    
                    tbody tr:last-child td:first-child {
                        border-bottom-right-radius: 8px;
                    }
                    
                    tbody tr:last-child td:last-child {
                        border-bottom-left-radius: 8px;
                    }
                    
                    tr {
                        page-break-inside: avoid;
                    }
                    
                    .signature-container {
                        display: flex;
                        justify-content: flex-end;
                        align-items: flex-start;
                        width: 100%;
                        margin-top: 5px;
                    }
                    
                    .signature-box {
                        width: 40%;
                        background: transparent;
                        border-radius: 8px;
                        text-align: left;
                        display: flex;
                        justify-content: flex-end;
                        align-items: center;
                    }
                    
                    .signature-slot {
                        width: ${signatureSlot.widthPx}px;
                        height: ${signatureSlot.heightPx}px;
                        min-width: ${signatureSlot.widthPx}px;
                        min-height: ${signatureSlot.heightPx}px;
                        max-width: ${signatureSlot.widthPx}px;
                        max-height: ${signatureSlot.heightPx}px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                        flex-grow: 0;
                        position: relative;
                    }
                    
                    .signature-slot img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        object-position: center;
                        display: block;
                    }
                    
                    .description-box {
                        width: 40%;
                        height: 120px;
                        background: transparent;
                        padding-right: 25px;
                        border-radius: 8px;
                        text-align: right;
                        margin-left: auto;
                    }
                    
                    .footer-box {
                        position: absolute;
                        bottom: 40px;
                        left: 50%;
                        transform: translateX(-50%);
                        text-align: center;
                        width: 80%;
                    }
                </style>
            </head>
            <body style="position: relative;">
                <div class="a4">
                    <div class="upper-panel">
                        <div class="upper-panel-details" style="${upperPanelDetailsInlineStyle}">
                            <!-- Company Info Section -->
                            <div class="header-company-info" style="${companyInlineStyle}">
                                <div style="color: #000000; font-family: ${fontFamilyCss}; font-size: ${textSize}px; line-height: 1.5; text-align: ${aling};">
                                    <div class='haktsaa' style="color: #000000; font-size: ${companyInfoTextSize}px; text-align: ${aling};">${client.name || 'חברת דוגמה'}</div>
                                    ${headerRightText ? `<div class='haktsaa' style="color: #000000; font-size: ${companyInfoTextSize}px; text-align: ${aling}; margin-bottom: 5px;">${headerRightText}</div>` : ''}
                                    <div class='haktsaa' style="color: #000000; font-size: ${companyInfoTextSize}px; text-align: ${aling};"><span style="font-weight: bold;">${idLabel}:</span> ${client.vatClientNumber?.trim() || client.caseId || '123456789'}</div>
                                    ${client.location ? `<div class="title-details" style="text-align: ${aling}; color: #000000; font-size: ${companyInfoTextSize}px;"><span style="font-weight: bold;">כתובת</span>: ${client.location}</div>` : ''}
                                    ${client.phoneNumber ? `<div class='haktsaa' style="text-align: ${aling}; color: #000000; font-size: ${companyInfoTextSize}px;"><span style="font-weight: bold;">טל1</span>: ${client.phoneNumber}</div>` : ''}
                                    ${client.secondaryPhoneNumber ? `<div class='haktsaa' style="text-align: ${aling}; color: #000000; font-size: ${companyInfoTextSize}px;"><span style="font-weight: bold;">טל2</span>: ${client.secondaryPhoneNumber}</div>` : ''}
                                </div>
                            </div>
                            
                            <!-- Logo Slot -->
                            ${hasLogo ? `
                            <div class="logo-slot" style="${logoInlineStyle}">
                                ${logo}
                            </div>
                            ` : ''}
                            
                            <!-- Header Text Section -->
                            <div class="header-text-section" style="${headerInlineStyle}">
                                ${headerLeftText ? `
                                <div style="color: #000000; font-family: ${fontFamilyCss}; font-size: ${textSize}px; line-height: 1.5; text-align: ${aling === 'right' ? 'left' : 'right'};">
                                    <div class='haktsaa' style="color: #000000; font-size: ${companyInfoTextSize}px; text-align: ${aling === 'right' ? 'left' : 'right'};">${headerLeftText}</div>
                                </div>
                                ` : ''}
                            </div>
                        </div>
                    </div>
                    
                    <div class='haktsaa1' style='width: 80%; text-align: center; margin-top: 20px; font-size: ${documentTitleTextSize}px;'>${title} מספר דוגמא-001</div>
                    <div class='title-from-to' style='width: 80%; text-align: center;' font-size: ${documentTitleTextSize}px;'>טיוטה</div>
                    <div class="title-from-to" style="width: 80%; text-align: right;"><strong>לכבוד-</strong></div>
                    
                    <div style="display: flex; flex-direction: column; width: 100%; margin-bottom: 10px;">
                        <div style="display: flex; align-items: center; justify-content: center;">
                            <div class="from-to-divs" style="width: 80%; height: 80%; border-radius: 10px; position: relative; justify-content: center; background-color: #ffffff;">
                                ${dateAlign === 'left' ? `
                                <div style="display: flex; width: 100%; justify-content: space-between; align-items: center; gap: 10px;">
                                    <div class="title-inner" style="text-align: right; color: ${titleTextColor || '#000000'};">
                                        ${client.clientName || 'לקוח דוגמה'}
                                    </div>
                                    <div class="title-details" style="text-align: left; ${dateStyle}">
                                        <span style="font-weight: bold;">תאריך</span>: ${new Date().toLocaleDateString('he-IL')}
                                    </div>
                                </div>
                                <div style="display: flex; width: 100%; gap: 10px; ">
                                    <div style="flex: 1; display: flex; flex-direction: column; align-items: flex-start;">
                                        <div class="title-details" style="text-align: right;">
                                            <span style="font-weight: bold;">ח"פ\\ע"מ</span>: ${client.clientCaseId || '987654321'}
                                        </div>
                                        ${client.clientLocation ? `<div class="title-details" style="text-align: right;"><span style="font-weight: bold;">כתובת</span>: ${client.clientLocation}</div>` : ''}
                                        ${client.phoneNumber ? `<div class="title-details" style="text-align: right;"><span style="font-weight: bold;">מספר טלפון</span>: ${client.phoneNumber}</div>` : ''}
                                    </div>
                                    <div style="flex: 1;"></div>
                                </div>
                                ` : `
                                <div class="title-inner" style="width: 80%; text-align: right; color: ${titleTextColor || '#000000'};">
                                    ${client.clientName || 'לקוח דוגמה'}
                                </div>
                                <div style="display: flex; flex-direction: column; width: 80%;">
                                    <div class="title-details" style="width: 100%; text-align: ${dateAlign || 'right'}; ${dateStyle}">
                                        <span style="font-weight: bold;">תאריך</span>: ${new Date().toLocaleDateString('he-IL')}
                                    </div>
                                    <div class="title-details" style="width: 100%; text-align: right;">
                                        <span style="font-weight: bold;">ח"פ\\ע"מ</span>: ${client.clientCaseId || '987654321'}
                                    </div>
                                    ${client.clientLocation ? `<div class="title-details" style="width: 100%; text-align: right;"><span style="font-weight: bold;">כתובת</span>: ${client.clientLocation}</div>` : ''}
                                    ${client.phoneNumber ? `<div class="title-details" style="width: 100%; text-align: right;"><span style="font-weight: bold;">מספר טלפון</span>: ${client.phoneNumber}</div>` : ''}
                                </div>
                                `}
                            </div>
                        </div>
                    </div>
                    
                    ${shouldShowTable ? `
                        <div class="section">
                            <div class="inner-section">
                                <div class="title-inner-payment" style="text-align: right; color: ${titleTextColor || '#000000'};">
                                    פריטים
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>מק״ט</th>
                                            <th>תיאור הפריט</th>
                                            ${itemsDisplayMode !== 'descriptionOnly' ? '<th>כמות</th>' : ''}
                                            <th>מחיר</th>
                                           ${itemsDisplayMode !== 'descriptionOnly' ? '<th>הנחה</th>' : ''}
                                            <th>סכום הנחה</th>
                                            <th>סה״כ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${sampleItems.map(item => `
                                            <tr>
                                                <td>${item.makat}</td>
                                                <td>${item.name}</td>
                                                ${itemsDisplayMode !== 'descriptionOnly' ? `<td>${item.quantity}</td>` : ''}
                                                <td>₪ ${item.price}</td>
                                                ${itemsDisplayMode !== 'descriptionOnly' ? `<td>${item.discount}%</td>` : ''}
                                                <td>₪ (0.00)</td>
                                                <td>₪ ${item.priceAfterDiscount}</td>
                                            </tr>
                                        `).join('')}
                                    </tbody>
                                </table>
                                
                                <div class="section" style="margin-top: 30px; ${totalsLayout === 'vertical' ? 'display: flex; justify-content: flex-end; margin-bottom: 5px;' : ''}">
                                    ${totalsLayout === 'vertical' ? `
                                        <!-- Vertical Layout: Clean Table Style -->
                                        <div style="width: 50%; background: #ffffff; border: 2px solid #162473; border-radius: 12px; overflow: hidden; direction: ${direction};">
                                            <table style="width: 100%; border-collapse: collapse; direction: ${direction};">
                                                <tbody>
                                                    <tr style="border-bottom: 1px solid #e8eaed;">
                                                        <td style="padding: 10px 15px; text-align: ${aling}; font-size: 13px; font-weight: 600; color: #162473; background-color: #f8f9fa; width: 50%;">סה״כ ללא מע״מ</td>
                                                        <td style="padding: 10px 15px; text-align: center; font-size: 15px; font-weight: 500; color: #000000; width: 50%;">₪ ${totalPrice.toFixed(2)}</td>
                                                    </tr>
                                                    <tr style="border-bottom: 1px solid #e8eaed;">
                                                        <td style="padding: 10px 15px; text-align: ${aling}; font-size: 13px; font-weight: 600; color: #162473; background-color: #f8f9fa; width: 50%;">${(vatRate * 100)}% מע״מ</td>
                                                        <td style="padding: 10px 15px; text-align: center; font-size: 15px; font-weight: 500; color: #000000; width: 50%;">₪ ${tax.toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style="padding: 12px 15px; text-align: ${aling}; font-size: 14px; font-weight: 700; background-color: #162473; color: #ffffff; width: 50%;">סה״כ לתשלום</td>
                                                        <td style="padding: 12px 15px; text-align: center; font-size: 17px; font-weight: 700; color: #ffffff; background-color: #162473; width: 50%;">₪ ${totalPriceWithTax.toFixed(2)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ` : `
                                        <!-- Horizontal Layout: Original Styled Bar -->
                                        <div class="inner-section" style="margin: 0 0 20px 0; height: 50px; position: relative; background: radial-gradient(307.62% 62.94% at 50% 50%, rgba(255, 252, 246, 0.44) 0%, #FFFCF6 100%); border-bottom: 10px solid #162473; border-bottom-left-radius: 15px; border-bottom-right-radius: 15px;">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="51" height="52" viewBox="0 0 51 52" fill="none"
                                                style="position: absolute; top: 0; left: 0; transform: scaleX(-1); clip-path: inset(0 round 8px);">
                                                <path
                                                    d="M5.23305 0L55.9996 0V48.721C55.9996 48.721 56.0926 26.778 27.734 25.7552C-0.624635 24.7324 5.23305 0 5.23305 0Z"
                                                    fill="#162473" />
                                            </svg>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="51" height="52" viewBox="0 0 51 52" fill="none"
                                                style="position: absolute; bottom: -2; right: 0; clip-path: inset(0 round 8px); transform: rotate(90deg);">
                                                <path
                                                    d="M5.23305 0L55.9996 0V48.721C55.9996 48.721 56.0926 26.778 27.734 25.7552C-0.624635 24.7324 5.23305 0 5.23305 0Z"
                                                    fill="#162473" />
                                            </svg>
                                            <div style="display: flex; justify-content: space-between; align-items: center; height: 100%; padding: 0 20px;">
                                                <div style="text-align: center; width: 33.33%;">
                                                    <div style="font-size: 14px; font-weight: 600; color: #162473;">סה״כ לתשלום</div>
                                                    <div style="font-size: 16px; font-weight: 400; color: #000000;">₪ ${totalPriceWithTax.toFixed(2)}</div>
                                                </div>
                                                <div style="text-align: center; width: 33.33%;">
                                                    <div style="font-size: 14px; font-weight: 600; color: #162473;">${(vatRate * 100)}% מע״מ</div>
                                                    <div style="font-size: 16px; font-weight: 400; color: #000000;">₪ ${tax.toFixed(2)}</div>
                                                </div>
                                                <div style="text-align: center; width: 33.33%;">
                                                    <div style="font-size: 14px; font-weight: 600; color: #162473;">סה״כ ללא מע״מ</div>
                                                    <div style="font-size: 16px; font-weight: 400; color: #000000;">₪ ${totalPrice.toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    `}
                                </div>
                            </div>
                        </div>
                        <div style="text-align: right; width: 100%; margin-right: 50px;">
                            <div style="font-size: 18px; font-weight: 600; color: #162473; margin-bottom: 5px;">פרטים</div>
                        </div>
                    ` : `

                        
                        <div class="section" style="margin-top: 50px; ${totalsLayout === 'vertical' ? 'display: flex; justify-content: flex-end;' : ''}">
                            ${totalsLayout === 'vertical' ? `
                                <!-- Vertical Layout: Clean Table Style -->
                                <div style="width: 50%; background: #ffffff; border: 2px solid #162473; border-radius: 12px; overflow: hidden; direction: ${direction};">
                                    <table style="width: 100%; border-collapse: collapse; direction: ${direction};">
                                        <tbody>
                                            <tr style="border-bottom: 1px solid #e8eaed;">
                                                <td style="padding: 10px 15px; text-align: ${aling}; font-size: 13px; font-weight: 600; color: #162473; background-color: #f8f9fa; width: 50%;">סה״כ ברוטו</td>
                                                <td style="padding: 10px 15px; text-align: center; font-size: 15px; font-weight: 500; color: #000000; width: 50%;">₪ ${totalPrice.toFixed(2)}</td>
                                            </tr>
                                            <tr style="border-bottom: 1px solid #e8eaed;">
                                                <td style="padding: 10px 15px; text-align: ${aling}; font-size: 13px; font-weight: 600; color: #162473; background-color: #f8f9fa; width: 50%;">ניכוי מס במקור</td>
                                                <td style="padding: 10px 15px; text-align: center; font-size: 15px; font-weight: 500; color: #000000; width: 50%;">₪ 0.00</td>
                                            </tr>
                                            <tr>
                                                <td style="padding: 12px 15px; text-align: ${aling}; font-size: 14px; font-weight: 700; background-color: #162473; color: #ffffff; width: 50%;">סה״כ שולם נטו</td>
                                                <td style="padding: 12px 15px; text-align: center; font-size: 17px; font-weight: 700; color: #ffffff; background-color: #162473; width: 50%;">₪ ${totalPrice.toFixed(2)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ` : `
                                <!-- Horizontal Layout: Original Styled Bar -->
                                <div class="inner-section" style="height: 50px; position: relative; background: radial-gradient(307.62% 62.94% at 50% 50%, rgba(255, 252, 246, 0.44) 0%, #FFFCF6 100%); border-bottom: 10px solid #162473; border-bottom-left-radius: 15px; border-bottom-right-radius: 15px;">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="51" height="52" viewBox="0 0 51 52" fill="none"
                                        style="position: absolute; top: 0; left: 0; transform: scaleX(-1); clip-path: inset(0 round 8px);">
                                        <path
                                            d="M5.23305 0L55.9996 0V48.721C55.9996 48.721 56.0926 26.778 27.734 25.7552C-0.624635 24.7324 5.23305 0 5.23305 0Z"
                                            fill="#162473" />
                                    </svg>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="51" height="52" viewBox="0 0 51 52" fill="none"
                                        style="position: absolute; bottom: -2; right: 0; clip-path: inset(0 round 8px); transform: rotate(90deg);">
                                        <path
                                            d="M5.23305 0L55.9996 0V48.721C55.9996 48.721 56.0926 26.778 27.734 25.7552C-0.624635 24.7324 5.23305 0 5.23305 0Z"
                                            fill="#162473" />
                                    </svg>
                                    <div style="display: flex; justify-content: space-between; align-items: center; height: 100%; padding: 0 20px;">
                                        <div style="text-align: center; width: 33.33%;">
                                            <div style="font-size: 14px; font-weight: 600; color: #162473;">סה״כ שולם נטו</div>
                                            <div style="font-size: 18px; font-weight: 400; color: #000000;">₪ ${totalPrice.toFixed(2)}</div>
                                        </div>
                                        <div style="text-align: center; width: 33.33%;">
                                            <div style="font-size: 14px; font-weight: 600; color: #162473;">ניכוי מס במקור</div>
                                            <div style="font-size: 18px; font-weight: 400; color: #000000;">₪ 0.00</div>
                                        </div>
                                        <div style="text-align: center; width: 33.33%;">
                                            <div style="font-size: 14px; font-weight: 600; color: #162473;">סה״כ ברוטו</div>
                                            <div style="font-size: 18px; font-weight: 400; color: #000000;">₪ ${totalPrice.toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                            `}
                        </div>
                    `}
                    
                    <div class="signature-container">
                        <div class="description-box">
                            <div style="font-size: 16px; font-weight: 400; color: #000000; line-height: 1.5; direction: rtl ">
                                ${customDescription ? customDescription.replace(/\n/g, '<br>') : 'תיאור מותאם לדוגמה'}
                            </div>
                        </div>
                        <div class="signature-box">
                            ${signaturePreview ? `
                            <div class="signature-slot">
                                <img src="${signaturePreview}" alt="Signature" />
                            </div>
                            ` : ''}
                        </div>

                    </div>
                    
                    <div class="footer-box">
                        <div style="height: auto; display: flex; justify-content: center; align-items: center; direction: rtl">
                            ${customFooter ? customFooter.replace(/\n/g, '<br>') : 'כותרת תחתונה מותאמת לדוגמה'}
                        </div>
                    </div>
                    
                    ${footer}
                </div>
            </body>
            </html>
        `;
        
        return htmlContent;
    }, [client, settings, selectedDocumentType, signaturePreview, profilePreview, defaultItem]);
    
    return (
        <Box sx={{ 
            flex: 1, 
            overflow: 'hidden',
            border: '1px solid #e8eaed',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            height: '80%',
            display: 'flex',
            flexDirection: 'column'
        }} dir={direction}>
            {generatePreviewHtml ? (
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '10px',
                    backgroundColor: '#f8f9fa'
                }}>
                    <Box sx={{
                        width: '210mm',
                        minHeight: '297mm',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <iframe
                            srcDoc={generatePreviewHtml}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                minHeight: '297mm'
                            }}
                            title="Document Preview"
                        />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    backgroundColor: '#f8f9fa'
                }} dir={direction}>
                    <Typography sx={{ color: '#666666', fontSize: '14px' }} dir={direction}>
                        {t('GeneratingPreview') || 'טוען תצוגה מקדימה...'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default DocumentPreview;
